import * as THREE from "three";
import { pick } from "../core/util.js";
import { fitToHeight } from "../core/modelFit.js";
import { CharacterAnimator } from "./CharacterAnimator.js";
import { MODEL_FACE_OFFSET } from "./Player.js";
import { FEATURES } from "../config/features.js";

const SKEL_ANIM = ["anim/skel_general", "anim/skel_movement"];
// NOTE: the KayKit Character Animations combat clips carry ROOT MOTION (they
// translate the hips), which floats/locks retargeted bodies — reverted until we
// strip root motion or use in-place variants. These aliases keep the enemy defs
// stable while pointing back at the safe idle/walk set.
const SKEL_MELEE = SKEL_ANIM;
const SKEL_CAST = SKEL_ANIM;
const SKEL_LARGE = ["anim/skel_large_general", "anim/skel_large_movement"];
// Attack is telegraphed: wind up, then the strike lands — giving the player a
// window to parry (block just before the strike) or dodge out of range.
const ATTACK_WINDUP = 0.45;
const ATTACK_RECOVER = 0.25;

/**
 * Enemy archetypes. A def may carry its own `modelKey` (else the theme's
 * enemyKey is used) and an `animLib` of registry keys for external animation
 * clips (KayKit skeletons keep clips in a separate rig file; Kenney monsters
 * embed their own, including a melee attack).
 */
export const ENEMY_DEFS = {
  // generic (Kenney-modelled per theme, embedded clips incl. attack)
  Husk:     { name: "Husk",     hp: 28, dmg: 6,  speed: 2.4, r: 0.55, color: 0x6aa84f, size: 1.0,  atkRange: 1.6, atkCd: 1.0 },
  Skeleton: { name: "Skeleton", hp: 22, dmg: 8,  speed: 3.4, r: 0.5,  color: 0xe0e0d0, size: 0.95, atkRange: 1.6, atkCd: 0.8 },
  Brute:    { name: "Brute",    hp: 70, dmg: 16, speed: 1.8, r: 0.8,  color: 0x8d4a4a, size: 1.5,  atkRange: 2.0, atkCd: 1.4 },
  Creep:    { name: "Creep",    hp: 34, dmg: 12, speed: 2.8, r: 0.55, color: 0x4a7d8d, size: 1.0,  atkRange: 1.6, atkCd: 1.1 },
  Bandit:   { name: "Bandit",   hp: 30, dmg: 10, speed: 3.0, r: 0.5,  color: 0x8a6a3a, size: 1.0,  atkRange: 1.6, atkCd: 0.9 },
  Wolf:     { name: "Wolf",     hp: 26, dmg: 11, speed: 4.2, r: 0.5,  color: 0x8a8f96, size: 0.9,  atkRange: 1.5, atkCd: 0.7 },
  Slime:    { name: "Slime",    hp: 40, dmg: 7,  speed: 1.6, r: 0.6,  color: 0x66cc88, size: 1.0,  atkRange: 1.4, atkCd: 1.0 },

  // KayKit skeletons (dungeon) — bodies + external Rig_Medium clips
  SkeletonWarrior: { name: "Skeleton Warrior", hp: 34, dmg: 10, speed: 3.0, r: 0.5,  color: 0xe0e0d0, size: 1.0,  atkRange: 1.7, atkCd: 0.9, modelKey: "enemies/skel_warrior", animLib: SKEL_MELEE, weaponKey: "skelw/axe" },
  SkeletonRogue:   { name: "Skeleton Rogue",   hp: 26, dmg: 12, speed: 4.0, r: 0.45, color: 0xe0e0d0, size: 0.95, atkRange: 1.5, atkCd: 0.7, modelKey: "enemies/skel_rogue",   animLib: SKEL_MELEE, weaponKey: "skelw/blade" },
  SkeletonMinion:  { name: "Skeleton Minion",  hp: 18, dmg: 6,  speed: 3.4, r: 0.45, color: 0xe0e0d0, size: 0.85, atkRange: 1.4, atkCd: 0.8, modelKey: "enemies/skel_minion",  animLib: SKEL_MELEE, weaponKey: "skelw/blade" },

  // ---- Ranged & magic attackers (fire projectiles; kite to keep distance) ----
  SkeletonArcher:  { name: "Skeleton Archer",  hp: 24, dmg: 9,  speed: 2.8, r: 0.5,  color: 0xe0e0d0, size: 0.98, atkRange: 11, atkCd: 1.5, ranged: true, projSpeed: 15, projColor: "#f0e6b0", modelKey: "enemies/skel_rogue",  animLib: SKEL_CAST, weaponKey: "skelw/blade" },
  SkeletonMage:    { name: "Skeleton Mage",    hp: 26, dmg: 12, speed: 2.6, r: 0.5,  color: 0xcfe0ff, size: 1.0,  atkRange: 10, atkCd: 1.7, ranged: true, magic: true, element: "frost", elementPower: 1.2, projSpeed: 12, modelKey: "enemies/skel_mage", animLib: SKEL_CAST, weaponKey: "skelw/staff" },
  Spider:          { name: "Web Spider",       hp: 30, dmg: 7,  speed: 3.4, r: 0.42, color: 0x6a5a4a, size: 0.58, atkRange: 9,  atkCd: 1.6, ranged: true, web: true, projSpeed: 13, projColor: "#e8f0ff", modelKey: "enemies/spider", faceOffset: Math.PI, scuttle: true },

  // Quaternius creature monsters (embedded clips) — mixed in when FEATURES.monsters is on
  OrcBrute:  { name: "Orc",        hp: 60, dmg: 15, speed: 2.2, r: 0.6,  color: 0x6a8a3a, size: 1.15, atkRange: 1.8, atkCd: 1.2, modelKey: "creatures/orc" },
  DemonImp:  { name: "Demon",      hp: 44, dmg: 15, speed: 3.0, r: 0.55, color: 0x8a3a3a, size: 1.0,  atkRange: 9,  atkCd: 1.5, ranged: true, magic: true, element: "fire", elementPower: 1.3, projSpeed: 12, aoe: 1.8, modelKey: "creatures/demon" },
  VaultYeti: { name: "Yeti",       hp: 92, dmg: 18, speed: 1.9, r: 0.75, color: 0xcfe0ff, size: 1.45, atkRange: 2.0, atkCd: 1.4, modelKey: "creatures/yeti" },
  SpikyBlob: { name: "Spiky Blob", hp: 44, dmg: 10, speed: 2.4, r: 0.55, color: 0x4a8a3a, size: 0.95, atkRange: 1.5, atkCd: 1.0, modelKey: "creatures/spikyblob" },

  // ---- Heavy chargers: BIGGER and FASTER than a walking player. Outrun them
  // only with a dodge-roll. Deep-floor threats. ----
  Revenant:   { name: "Revenant",   hp: 120, dmg: 22, speed: 7.2, r: 0.75, color: 0x9a6a3a, size: 1.45, atkRange: 2.0, atkCd: 1.3, charger: true, modelKey: "enemies/skel_warrior", animLib: SKEL_MELEE, weaponKey: "skelw/axe" },
  Juggernaut: { name: "Juggernaut", hp: 165, dmg: 25, speed: 7.0, r: 0.8,  color: 0xcfe0ff, size: 1.6,  atkRange: 2.1, atkCd: 1.5, charger: true, modelKey: "creatures/yeti" },

  // Mimic — a locked chest that springs to life. Fast, hits hard, drops its hoard on death.
  Mimic: { name: "Mimic", hp: 64, dmg: 17, speed: 3.6, r: 0.55, color: 0xc9a24b, size: 1.0, atkRange: 1.7, atkCd: 0.85, modelKey: "dungeon/chest_mimic" },

  // ---- BOSSES ----
  SkeletonGolem: { name: "Skeleton Golem", hp: 340, dmg: 24, speed: 3.4, r: 1.1, color: 0xd8d2c0, size: 2.6, atkRange: 3.0, atkCd: 1.6, boss: true, modelKey: "enemies/skel_golem", animLib: SKEL_LARGE, weaponKey: "skelw/golem_axe" },
  Necromancer:   { name: "The Necromancer", hp: 300, dmg: 20, speed: 3.0, r: 0.6, color: 0x7a4ea0, size: 1.35, atkRange: 12, atkCd: 1.5, boss: true, ranged: true, magic: true, element: "poison", elementPower: 1.4, projSpeed: 13, aoe: 2.0, reanimator: true, modelKey: "enemies/necromancer", animLib: SKEL_CAST, weaponKey: "skelw/staff" },
};

// Creature enemy pool, added to every biome's spawn list when monsters are enabled.
export const CREATURE_ENEMIES = ["OrcBrute", "DemonImp", "VaultYeti", "SpikyBlob"];
// Depth-gated advanced enemies: [minFloor, key, requiresMonstersFlag]
const DEPTH_ENEMIES = [
  [2, "SkeletonArcher", false], [2, "SkeletonMage", false], [2, "Spider", false],
  [5, "Revenant", false], [6, "Juggernaut", true],
];

export class Enemy {
  constructor(scene, registry, def, x, z, floor, fallbackModelKey, statMult = 1) {
    this.scene = scene;
    this.type = def;
    this.pos = new THREE.Vector3(x, 0, z);
    this.radius = def.r;
    this.boss = !!def.boss;
    // Scaling: floor 1 stays gentle; floors 2+ are tougher than before. Bosses
    // keep modest HP (real fight, not a slog) but damage scales to stay lethal.
    let hpf, dmgf;
    if (def.boss) { hpf = 1 + floor * 0.06; dmgf = 1 + floor * 0.09; }
    else if (floor <= 1) { hpf = 0.9; dmgf = 0.9; }               // level 1: easy
    else { hpf = 1 + floor * 0.24 + 0.15; dmgf = 1 + floor * 0.18 + 0.1; }
    this.maxHp = Math.round(def.hp * hpf * statMult);
    this.hp = this.maxHp;
    this.dmg = Math.round(def.dmg * dmgf * statMult);
    // deeper enemies also move a little faster (capped) — chase pressure scales too.
    // Bosses keep their fixed pace.
    this.spdMul = def.boss ? 1 : 1 + Math.min(0.4, Math.max(0, floor - 1) * 0.02);
    this.atkTimer = 0;
    this.atkState = "idle"; // idle | windup | recover
    this.atkT = 0;
    this.hurt = 0;
    this.dead = false;
    this.groundH = 0;       // eased floor height under the enemy (stairs/stories)
    // elemental status effects (set by CombatSystem via enchanted weapons)
    this.burnT = 0; this.burnDps = 0; this.poisonT = 0; this.poisonDps = 0;
    this.slowT = 0; this.slowMul = 1; this.stunT = 0; this._dotTick = 0;
    this.yaw = 0;
    this.lunge = 0;
    this.stagger = 0;
    this._buildMesh(registry, def.modelKey || fallbackModelKey, def.animLib, def.weaponKey);
  }

  _buildMesh(registry, modelKey, animLib, weaponKey) {
    this.mesh = new THREE.Group();
    if (registry.has(modelKey)) {
      const model = registry.instance(modelKey);
      fitToHeight(model, 1.7 * this.type.size);
      model.rotation.y = MODEL_FACE_OFFSET + (this.type.faceOffset || 0);   // per-model forward correction
      model.traverse((o) => { if (o.isMesh) o.castShadow = true; });
      this.mesh.add(model);
      // carry a weapon in the hand slot (KayKit skeletons share the hero rig)
      const hand = model.getObjectByName("handslotr") || model.getObjectByName("handr");
      if (hand && weaponKey && registry.has(weaponKey)) {
        const w = registry.instance(weaponKey);
        w.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        hand.add(w);
        this.weaponObj = w; // animated during the attack windup/strike
      }
      let clips = model.animations || [];
      if (animLib) for (const k of animLib) clips = clips.concat(registry.clips(k));
      if (clips.length) this.animator = new CharacterAnimator(model, clips);
    } else {
      const s = this.type.size;
      this._mat = new THREE.MeshStandardMaterial({ color: this.type.color, roughness: 0.8 });
      const body = new THREE.Mesh(new THREE.BoxGeometry(0.8 * s, 1.0 * s, 0.6 * s), this._mat);
      body.position.y = 0.7 * s; body.castShadow = true;
      const head = new THREE.Mesh(new THREE.BoxGeometry(0.5 * s, 0.5 * s, 0.5 * s), this._mat);
      head.position.y = 1.45 * s; head.castShadow = true;
      const eyeMat = new THREE.MeshBasicMaterial({ color: 0xff3030 });
      const eyeGeo = new THREE.BoxGeometry(0.1 * s, 0.1 * s, 0.05);
      const eL = new THREE.Mesh(eyeGeo, eyeMat); eL.position.set(-0.12 * s, 1.45 * s, 0.26 * s);
      const eR = new THREE.Mesh(eyeGeo, eyeMat); eR.position.set(0.12 * s, 1.45 * s, 0.26 * s);
      this.mesh.add(body, head, eL, eR);
    }
    this._makeHealthBar();
    this.mesh.position.copy(this.pos);
    this.scene.add(this.mesh);
  }

  _makeHealthBar() {
    this._hbCanvas = document.createElement("canvas");
    this._hbCanvas.width = 128; this._hbCanvas.height = 20;
    this._hbTex = new THREE.CanvasTexture(this._hbCanvas);
    const mat = new THREE.SpriteMaterial({ map: this._hbTex, transparent: true, depthTest: false });
    this.hbSprite = new THREE.Sprite(mat);
    this.hbSprite.scale.set(this.boss ? 3.2 : 1.6, this.boss ? 0.36 : 0.25, 1);
    this.hbSprite.position.y = 2.2 * this.type.size + 0.4;
    this.hbSprite.visible = !!this.boss;
    this.hbSprite.renderOrder = 998;
    this.mesh.add(this.hbSprite);
    this._drawHealthBar();
  }

  _drawHealthBar() {
    const c = this._hbCanvas.getContext("2d");
    c.clearRect(0, 0, 128, 20);
    c.fillStyle = "#000"; c.fillRect(0, 0, 128, 20);
    c.fillStyle = "#b5423a"; c.fillRect(2, 2, 124, 16);
    c.fillStyle = "#5fd35f"; c.fillRect(2, 2, 124 * Math.max(0, this.hp / this.maxHp), 16);
    this._hbTex.needsUpdate = true;
  }

  hitFlash() {
    this.hurt = 0.12;
    if (this._mat) { this._mat.emissive = new THREE.Color(0xffffff); this._mat.emissiveIntensity = 1; }
  }

  update(dt, player, level, combat) {
    if (this.dead) return;
    if (this.animator) this.animator.update(dt);
    this.hurt = Math.max(0, this.hurt - dt);
    if (this._mat) this._mat.emissiveIntensity = this.hurt * 8;
    this.atkTimer = Math.max(0, this.atkTimer - dt);
    this.lunge = Math.max(0, this.lunge - dt);
    this.stagger = Math.max(0, this.stagger - dt);

    // elemental status: damage-over-time (burn/poison) ticks + timers
    if (this.burnT > 0 || this.poisonT > 0) {
      this._dotTick -= dt;
      if (this._dotTick <= 0) {
        this._dotTick = 0.4;
        let dmg = 0;
        if (this.burnT > 0) dmg += this.burnDps * 0.4;
        if (this.poisonT > 0) dmg += this.poisonDps * 0.4;
        dmg = Math.max(1, Math.round(dmg));
        const col = this.burnT > 0 ? "#ff6a3d" : "#7bd645";
        const dead = this.applyDamage(dmg);
        if (combat && combat.effects) { combat.effects.text(this.pos, `${dmg}`, col); combat.effects.burst(this.pos, col, 4); }
        if (dead) { if (combat) combat.onEnemyKilled(this); return; }
      }
    }
    this.burnT = Math.max(0, this.burnT - dt);
    this.poisonT = Math.max(0, this.poisonT - dt);
    this.slowT = Math.max(0, this.slowT - dt);
    this.stunT = Math.max(0, this.stunT - dt);
    const spd = this.type.speed * (this.spdMul || 1) * (this.slowT > 0 ? this.slowMul : 1);

    const dx = player.pos.x - this.pos.x, dz = player.pos.z - this.pos.z;
    const d = Math.hypot(dx, dz) || 1;
    let moving = false;

    if (d < 16) this.yaw = Math.atan2(dx, dz);

    if (this.atkState === "windup") {
      this.atkT += dt;
      if (this.atkT >= ATTACK_WINDUP) {
        if (this.type.ranged) {
          // loose a bolt toward the player (it travels; the player can roll to dodge)
          if (combat.enemyRangedAttack) combat.enemyRangedAttack(this, player);
        } else if (Math.hypot(player.pos.x - this.pos.x, player.pos.z - this.pos.z) <= this.type.atkRange + 0.7) {
          combat.enemyAttack(this, player);   // melee whiffs if the player dodged out of reach
        }
        this.lunge = 0.16; this.atkState = "recover"; this.atkT = 0;
      }
    } else if (this.atkState === "recover") {
      this.atkT += dt;
      if (this.atkT >= ATTACK_RECOVER) { this.atkState = "idle"; this.atkTimer = this.type.atkCd; }
    } else if (d < 20 && this.stagger <= 0) {
      if (this.type.ranged && d < this.type.atkRange * 0.55 && d > 0.2) {
        // caster kites: back away to keep the player at range
        moving = true;
        const step = spd * dt, ox = this.pos.x, oz = this.pos.z;
        const bx = ox - (dx / d) * step, bz = oz - (dz / d) * step;
        if (!level.collidesWorld(bx, this.pos.z, this.radius)) this.pos.x = bx;
        if (!level.collidesWorld(this.pos.x, bz, this.radius)) this.pos.z = bz;
        if (this.atkTimer <= 0) { this.atkState = "windup"; this.atkT = 0; if (this.animator) this.animator.oneShot("attack"); }
      } else if (d > this.type.atkRange) {
        moving = true;
        let mvx = dx / d, mvz = dz / d;
        // prefer the prop-aware flow field at any range so we route around walls + objects
        const fd = level.flowDirWorld(this.pos.x, this.pos.z);
        if (fd) { mvx = fd.x; mvz = fd.z; }
        const step = spd * dt;
        const ox = this.pos.x, oz = this.pos.z;
        const tx = ox + mvx * step, tz = oz + mvz * step;
        if (!level.collidesWorld(tx, this.pos.z, this.radius)) this.pos.x = tx;
        if (!level.collidesWorld(this.pos.x, tz, this.radius)) this.pos.z = tz;
        // fully blocked? deflect the heading to slip around the obstacle
        if (this.pos.x === ox && this.pos.z === oz) {
          for (const ang of [0.7, -0.7, 1.4, -1.4, 2.2, -2.2]) {
            const c = Math.cos(ang), s = Math.sin(ang);
            const rx = mvx * c - mvz * s, rz = mvx * s + mvz * c;
            const sx = ox + rx * step, sz = oz + rz * step;
            if (!level.collidesWorld(sx, sz, this.radius)) { this.pos.x = sx; this.pos.z = sz; break; }
          }
        }
      } else if (this.atkTimer <= 0) {
        this.atkState = "windup"; this.atkT = 0;       // begin telegraphed attack
        if (this.animator) this.animator.oneShot("attack");
      }
    }

    // telegraph the swing on the held weapon (readable for parry timing)
    if (this.weaponObj) {
      let rx = 0;
      if (this.atkState === "windup") rx = -1.5 * (this.atkT / ATTACK_WINDUP);
      else if (this.atkState === "recover") rx = 0.95 * (1 - this.atkT / ATTACK_RECOVER);
      this.weaponObj.rotation.x = rx;
    }
    if (this.animator) this.animator.setMoving(moving);

    // soft separation against only nearby enemies (spatial hash), not all of them
    const neighbours = combat.nearbyEnemies ? combat.nearbyEnemies(this.pos.x, this.pos.z) : combat.enemies;
    for (const o of neighbours) {
      if (o === this || o.dead) continue;
      const ox = this.pos.x - o.pos.x, oz = this.pos.z - o.pos.z;
      const od = Math.hypot(ox, oz);
      if (od > 0 && od < this.radius + o.radius) {
        // separation must respect walls, or crowding pushes enemies through them
        const px = this.pos.x + (ox / od) * 0.6 * dt;
        const pz = this.pos.z + (oz / od) * 0.6 * dt;
        if (!level.collidesWorld(px, this.pos.z, this.radius)) this.pos.x = px;
        if (!level.collidesWorld(this.pos.x, pz, this.radius)) this.pos.z = pz;
      }
    }

    // ride the floor height (stairs/stories)
    if (level && level.groundY) {
      const gy = level.groundY(this.pos.x, this.pos.z);
      this.groundH += (gy - this.groundH) * Math.min(1, dt * 10);
    }
    // procedural lunge on attack — a big heavy lurch for bosses
    const lungeAmt = this.lunge > 0 ? Math.sin((this.lunge / 0.18) * Math.PI) * (this.boss ? 0.6 : 0.25) : 0;
    // procedural scuttle for rigged-but-unanimated models (e.g. the spider):
    // a fast vertical hop + body rock while moving so it reads as scuttling legs.
    let scuttleY = 0;
    if (this.type.scuttle) {
      if (moving) {
        const s = performance.now() * 0.02 + this.pos.x;
        scuttleY = Math.abs(Math.sin(s)) * 0.13 * this.type.size;
        this._scuttleRoll = Math.sin(s * 2) * 0.16;
      } else {
        this._scuttleRoll = (this._scuttleRoll || 0) * 0.85;
      }
    }
    this.mesh.position.set(
      this.pos.x + Math.sin(this.yaw) * lungeAmt,
      this.groundH + scuttleY + Math.sin(performance.now() * 0.004 + this.pos.x) * 0.06,
      this.pos.z + Math.cos(this.yaw) * lungeAmt
    );
    this.mesh.rotation.y = this.yaw;
    if (this.type.scuttle) this.mesh.rotation.z = this._scuttleRoll || 0;
    this.hbSprite.visible = this.boss || this.hp < this.maxHp;
  }

  // ---- elemental status setters (called by CombatSystem) ----
  applyBurn(dps, t) { this.burnT = Math.max(this.burnT, t); this.burnDps = Math.max(this.burnDps, dps); }
  applyPoison(dps, t) { this.poisonT = Math.max(this.poisonT, t); this.poisonDps = Math.max(this.poisonDps, dps); }
  applySlow(mul, t) { this.slowT = Math.max(this.slowT, t); this.slowMul = mul; }
  applyStun(t) { this.stunT = Math.max(this.stunT, t); this.stagger = Math.max(this.stagger, t); this.atkState = "idle"; }

  applyDamage(amt) {
    this.hp -= amt;
    this.hitFlash();
    this._drawHealthBar();
    if (this.hp <= 0) this.dead = true;
    return this.dead;
  }

  dispose() {
    this.scene.remove(this.mesh);
    this._hbTex?.dispose();
  }
}

/** Spawn a wave from the level's spawn points using the theme's enemy pool. */
export function spawnEnemies(scene, registry, level, floor, player, theme, baseCount, perFloor, difficulty = { countMult: 1, statMult: 1 }) {
  const enemies = [];
  const count = Math.max(1, Math.round((baseCount + floor * perFloor) * difficulty.countMult));
  const pts = [...level.spawnPoints];
  for (let i = pts.length - 1; i > 0; i--) { const j = (Math.random() * (i + 1)) | 0; [pts[i], pts[j]] = [pts[j], pts[i]]; }
  let pool = theme.enemies && theme.enemies.length ? [...theme.enemies] : Object.keys(ENEMY_DEFS);
  if (FEATURES.monsters) pool = pool.concat(CREATURE_ENEMIES);   // mix in creatures when enabled
  // depth introduces ranged/magic attackers, then heavy chargers
  for (const [minFloor, key, needsMonsters] of DEPTH_ENEMIES) {
    if (floor >= minFloor && (!needsMonsters || FEATURES.monsters)) pool.push(key);
  }
  const OFFS = [[0, 0], [1.2, 0], [-1.2, 0], [0, 1.2], [0, -1.2], [1.2, 1.2], [-1.2, -1.2], [1.2, -1.2], [-1.2, 1.2]];
  let placed = 0;
  for (let i = 0; i < pts.length && placed < count; i++) {
    const sp = pts[i];
    const def = ENEMY_DEFS[pick(pool)] || ENEMY_DEFS.Husk;
    const w = level.gridToWorld(sp.gx, sp.gy);
    // never spawn inside a prop collider (e.g. a chest) — nudge to a free spot, else skip
    let sx = null, sz = null;
    for (const [ox, oz] of OFFS) {
      const tx = w.x + ox, tz = w.z + oz;
      if (!level.collidesWorld(tx, tz, def.r)) { sx = tx; sz = tz; break; }
    }
    if (sx === null) continue;
    const e = new Enemy(scene, registry, def, sx, sz, floor, theme.enemyKey, difficulty.statMult);
    e.groundH = level.groundY ? level.groundY(sx, sz) : 0;   // start at floor height
    e.pos.y = e.groundH; e.mesh.position.y = e.groundH;
    enemies.push(e);
    placed++;
  }
  return enemies;
}
